<script setup lang="ts">
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSharedUi } from '@/composables/useSharedUi'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import CustomButton from '@/components/customButton/CustomButton.vue'

const isActive = ref(false)
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { hideMessageMenu } = useSharedUi()
const { createUmsokn } = useUmsokn()

const props = defineProps<{
  setLoading?: (value: boolean) => void
  /** Optional: override target path if you really want a different route */
  nextPath?: string
}>()

// New launagreiðandi app id (legacy = 9)
const APP_ID = 9

async function openApplication() {
  isActive.value = true
  try {
    props.setLoading?.(true)
    const newApp = await createUmsokn(APP_ID)
    if (newApp) {
      hideMessageMenu()

      // If BE already has a step, use it; otherwise start at 1
      const step = newApp.currentStep && newApp.currentStep > 0 ? newApp.currentStep : 1

      // Decide target path: either prop override or stay on current path
      const targetPath = props.nextPath ?? route.path

      props.setLoading?.(false)

      // Update only the query; avoid pushing to a non-existent path
      router.push({ path: targetPath, query: { step: String(step) } })
    } else {
      console.error('Application was not created.')
      props.setLoading?.(false)
    }
  } catch (err) {
    console.error('Failed to create application:', err)
    props.setLoading?.(false)
  }
}
</script>

<template>
  <div>
    <v-container class="scrollview">
      <v-row justify="center">
        <v-col>
          <h1 :class="[isActive ? 'active' : '', 'heading']">
            {{ t('umsokn_nyr_launagreidandi_step0_header1') }}
          </h1>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col md="7">
          <p>{{ t('umsokn_nyr_launagreidandi_step0_begin_text') }}</p>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col md="6">
          <p style="font-weight:bold">
            {{ t('umsokn_nyr_launagreidandi_step0_disclaimer') }}
          </p>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col md="7">
          <p style="font-size:16px">
            {{ t('umsokn_nyr_launagreidandi_step0_disclaimer_text') }}
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="d-flex justify-center">
          <CustomButton @click="openApplication">
            {{ t('sereignar_umsokn_begin_button') }}
          </CustomButton>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<style scoped>
h1 {
  color: #194458;
  font-size: 55px;
  font-weight: 900;
  line-height: 75px;
  text-align: center;
}
p {
  color: #194458;
  font-size: 21px;
  line-height: 34px;
  text-align: center;
  margin-bottom: 40px;
}
.heading {
  position: relative;
  font-size: 55px;
}
.active {
  transition: all 0.8s linear;
  top: -900px;
  height: 20px;
  color: #194458;
  font-size: 16px;
  font-weight: bold;
  line-height: 20px;
}
@media (max-width: 768px) {
  .scrollview {
    overflow-y: scroll;
    padding-bottom: 91px;
  }
}
</style>
